import { PaymentType } from '~/common/enums/payments.enum';
import { MessageKeys } from '~/domains/payments/deposits/components/consts';

export default {
  newBadge: 'NEW',
  addNewCard: 'Add a New Card',
  paymentTypes: {
    DEBIT_CARD: 'Debit Card', // a special case for withdrawal
    [PaymentType.AEROPAY]: 'Online Banking',
    [PaymentType.CARD]: 'Debit/Credit',
    [PaymentType.PAYPAL]: 'PayPal',
    [PaymentType.VIPPREFERRED]: 'Online Banking',
    [PaymentType.PAPER_CHECK]: 'Check',
    [PaymentType.VENMO]: 'Venmo',
    [PaymentType.SKRILL]: 'Skrill',
    [PaymentType.WHOP]: 'Crypto',
    [PaymentType.WIRE]: 'Wire Transfer',
    [PaymentType.EDGE_BOOST]: 'EDGE Boost',
    [PaymentType.WHOP_CRYPTO]: 'Crypto', // <!-- COPY -->
  },
  cardSelectLabel: 'Ending in {{lastDigits}} | Exp {{expMonth}}/{{expYear}}',
  cardSelect: {
    label: 'Ending in {{lastDigits}}',
    processingFee: '{{processingFee}}% processing fee',
    expiration: 'Exp {{expMonth}}/{{expYear}}',
  },
  deleteCardModal: {
    removeCard: 'Remove card',
    removeCardCopy: 'Are you sure you want to remove your card ending in {{lastDigits}}?',
    cancel: 'Cancel',
    remove: 'Remove',
    success: { message: 'Card deleted successfully.' },
    error: {
      title: 'Error',
      message: 'We were unable to delete this saved card. Please contact customer support.',
    },
  },
  currentBalance: 'Current Balance',
  withdrawableBalance: 'Withdrawable Balance',
  withdrawableBalanceTooltipTitle: 'Withdrawable Balance',
  withdrawableBalanceTooltipText:
    'The amount available to cash out. Excludes Splash credits, play through cash, and funds eligible for refund.',
  refundableBalance: 'Refundable balance',
  refundableBalanceTooltipTitle: 'Refundable balance',
  refundableBalanceTooltipText:
    'Funds that were deposited and not played through. Must be withdrawn to the same payment method that was used for the deposit.',
  // Refund screen translations
  refund: {
    title: 'REQUEST REFUNDS',
    copy: 'Select the refund you want to receive, then tap the "Refund" button to submit your request.',
    availableRefunds: 'Available Refunds',
    noRefundsAvailable: 'No refundable balance available.',
    selectRefund: 'Refund',
    refundAmount: 'Refund {{amount}}',
    // eslint-disable-next-line no-template-curly-in-string
    balanceAvailable: 'You have ${{dollars}}.{{cents}} in refunds available.',
    success: {
      title: 'Success!',
      message: 'Your refund is being processed.',
      messageWithRemaining: 'Your refund is being processed.',
    },
    error: {
      title: 'Error',
      message: 'Failed to process refund request. Please try again.',
    },
  },
  // Refund payment methods component translations
  refundPaymentMethods: {
    consistencyError: {
      title: 'Unable to Process Refund',
      description:
        "We're experiencing an issue with your refundable balance. Please <link>contact support</link> for assistance.",
    },
  },
  // Payment method display names
  paymentMethods: {
    paypal: 'PayPal',
    venmo: 'Venmo',
    skrill: 'Skrill',
    bankAccount: 'Bank account',
    debitCard: 'Debit card',
  },
  credits: 'Credits',
  creditsTooltipTitle: 'Credits',
  creditsTooltipText: 'Non-withdrawable funds provided by Splash Sports for gameplay only.',
  continue: 'Continue',
  transactionHistory: {
    noTransactions: 'No transactions found',
    pending: 'Pending Transactions',
    recent: 'Recent Transactions',
    all: 'All Transactions',
    type: 'Type',
    transId: 'Transaction ID',
    date: 'Date',
    balance: 'Balance',
  },
  deposits: {
    bonusBanner: {
      details: 'Details',
      predictionMarketsWarning:
        'Bonus credits cannot be used on Splash Predict. Trades on Splash Predict do not count toward play through requirement.',
    },
    aeropay: {
      challenge: {
        error: {
          title: 'Aeropay Verify Account Error',
          message: 'There was an issue with the Aeropay verification. Please try again later.',
        },
      },
      bankAccountLinkCreated: {
        title: 'Bank account linked',
        message: 'Processing your deposit...',
      },
      continueWithAeropay: 'Continue to deposit',
      creatingBankAccountLink: {
        title: 'Creating bank account link',
        message: 'We are creating a bank account link for you. This will only take a moment.',
      },
      createDeposit: {
        title: 'Almost there!',
        message: 'We are processing your deposit.',
      },
      verify: {
        title: 'Verify your account',
        message:
          'For your security, we sent a 6-digit verification code to the phone number or email on your account. Enter it below to continue with your deposit.',
      },
    },
    completed: {
      title: 'Deposit successful',
      fundsAdded: `{{amount}} has been added to your account`,
      bonusCreditsMessage: `Includes {{bonusAmount}} credits from deposit match.`,
      balanceUnavailable: `Your balance is currently unavailable`,
      continue: 'Continue',
      exploreContests: 'Explore contests',
      balanceBreakdown: {
        total: 'Total',
        totalLabel: 'Balance',
        cash: 'Cash',
        cashLabel: 'Balance',
        credits: 'Credits',
        creditsLabel: 'Balance',
      },
    },
    savedDepositMethods: 'Saved deposit methods',
    otherDepositMethods: 'Other deposit methods',
    recommendedDepositMethods: 'Recommended',
    recommendedDepositDescription:
      'Deposit with online banking to unlock free and instant withdrawals, no account or routing numbers needed.',
    connectAnotherBank: 'Connect another bank account',
    wireTransfer: {
      tileTag: '{{amount}} minimum',
      title: 'Wire Transfer',
      intro:
        'Provide the following information to your bank to initiate a wire transfer to Splash Sports. US banks only.',
      warningTitle: '{{amount}} minimum.',
      warningBody:
        'Any transfer of less than {{amount}} will be returned. Please use Online Banking (Aeropay) for smaller deposits.',
      importantInformation: 'Important Information',
      notices: {
        minimum: 'Wire Transfer Minimum — {{amount}}',
        nameMatch:
          'The name on your bank account must match the name on your Splash Sports account',
        deadline:
          'All wires must be received Monday – Friday by 4:00 PM EST to be posted the same day.',
        location: 'You must be in a valid location for the credit to be posted to your account.',
      },
      bankInformation: 'Splash Sports Bank Information',
      bank: {
        receivingBankName: { label: 'Receiving Bank Name', value: 'MVB Bank, Inc.' },
        receivingBankAddress: {
          label: 'Receiving Bank Address',
          value: '301 Virginia Avenue, Fairmont, WV 26554',
        },
        routingNumber: { label: 'ABA / Routing Number', value: '051504597' },
        accountNumber: { label: 'Account Number', value: '1919364' },
        beneficiaryTitle: {
          label: 'Beneficiary Account Title',
          value: 'Splash Player Reserve LLC',
        },
        beneficiaryAddress: {
          label: 'Beneficiary Address',
          value: '2261 Market Street, Suite 86366, San Francisco, CA 94114',
        },
        reference: { label: 'Reference', fallback: 'Your Splash Sports email address' },
      },
      closing:
        'To ensure proper posting, please adhere to the exact wire transfer instructions provided above.',
      done: 'Done',
    },
    form: {
      paymentType: {
        label: 'Payment Type',
        title: 'DEPOSIT METHOD',
        copy: 'Select your deposit method. You will only be able to withdraw funds using methods that you’ve used to deposit funds.',
      },
      details: {
        title: 'TRANSACTION DETAILS',
        copy: 'Enter your deposit amount and transaction details.',
        copyLinkAeropay:
          "Enter your deposit amount. You'll be redirected to our trusted ACH partner, Aeropay, to link your online banking account.",
      },
      continue: 'Continue',
      amount: {
        label: 'Deposit Amount',
        custom: 'Custom',
        placeholder: 'Add Custom Amount',
        errors: {
          mustNotBeEmpty: 'Please specify a custom deposit amount',
          mustBeMoreThan: 'Deposit amount must be at least {{amount}}',
          mustBeLessThan: 'Deposit amount must be at most {{amount}}',
        },
      },
      card: {
        label: 'Select Card',
      },
      cardDetails: 'Card Details',
      cardholderName: {
        label: 'Cardholder Name',
        placeholder: 'Cardholder Name',
        error: 'Please enter a valid cardholder name',
      },
      cardNumber: {
        label: 'Card Number',
        placeholder: '1234 1234 1234 1234',
        error: 'Please enter a valid card number',
      },
      expDate: {
        label: 'Exp Date',
        placeholder: 'MM/YY',
        error: 'Please enter a valid expiration date',
      },
      cvv: {
        label: 'CVV',
        placeholder: '###',
        error: 'Please enter a valid CVV',
      },
      confirmCVV: {
        label: 'Confirm CVV',
        placeholder: '###',
        description: 'CVV is the 3 or 4 digit number on the back of your card',
        error: 'Please enter a valid CVV',
      },
      deposit: 'Deposit {{amount}}',
      youWillBeRedirectedToPaypal: 'You will be redirected to PayPal to complete your deposit',
      youWillBeChargedBySplash: 'You will be charged a total of {{amount}} by Splash Sports.',
      cryptoNonRefundable: 'Crypto deposits are non-refundable',
    },
    warnings: {
      missingKYC: 'We need a little more information from you before you can deposit.',
    },
    errors: {
      default: '{{type}} Deposit Error',
      paypal: 'PayPal Deposit Error',
      skrill: 'Skrill Deposit Error',
      venmo: 'Venmo Deposit Error',
      crypto: 'Crypto Deposit Error',
      savedCard: 'Saved Card Deposit Error',
      newCard: 'Card Deposit Error',
      vipp: 'VIPP Deposit Error',
      deposit: 'Deposit Error',
      paysafe: {
        incorrectAddressTitle: 'Incorrect Address',
        incorrectAddressMessage: 'Please ensure that the billing address is correct.',
        cardTypeNotSupportedTitle: 'Card type not supported',
        cardTypeNotSupportedMessage:
          'The card type that you have entered is not currently supported',
        generalTitle: 'Deposit Error - Paysafe',
        generalMessage:
          'Your deposit could not be processed. Please make sure the information entered is correct and contact support if you think there is an issue.',
      },
      blockedByFeatureFlag: 'Unfortunately, you are not currently allowed to make a deposit.',
      pleaseNote: 'Please note:',
      actionRequired: 'Action required!',
      blockedByLocation:
        '<pleaseNote/> Deposits can only be made from an <eligibleLocation/>. If you believe you’ve received this message in error, please contact <customerSupportLink/>.',
      locationServicesDisabled:
        '<actionRequired/> You must <locationServices/> to be able to make a deposit, so that we can verify you are in an <eligibleLocation/>.',
      unavailablePaymentType:
        'Unfortunately, depositing with {{type}} is not currently available at your location.',
      notifications: {
        [MessageKeys.REJECTED]: 'There was an issue processing your {{type}} deposit.',
      },
      links: {
        eligibleLocation: 'eligible location',
        customerSupport: 'customer support',
        locationServices: 'share your location',
      },
      noAvailableMethods:
        "Depositing isn't supported in your current location. Try refreshing the page. If you think this is incorrect, please <customerSupportLink>contact support</customerSupportLink>.",
      aeropay: {
        title: 'Deposit Error',
        general:
          'Your deposit could not be processed. Please make sure the information entered is correct and contact support if you think there is an issue.',
        maxAccounts:
          'You have reached the maximum number of Aeropay accounts. Remove an account to add a new one.',
        bankLinkFailed:
          "We couldn't link your bank account through Aeropay. Please try again or use a different bank account.",
        declines: {
          bankDecline:
            'Your bank declined this transfer through Aeropay. Try a different bank or payment method, or contact your bank.',
          limitExceeded:
            'Aeropay declined this deposit because the amount is over your limit. Try a smaller amount.',
          insufficientFunds:
            "Aeropay declined this deposit because your bank account doesn't have enough funds available. Try a smaller amount or a different bank account.",
          accountRestricted:
            "Aeropay can't accept deposits from this bank account right now. Try a different payment method or contact support.",
          bankNotVerified:
            "Aeropay couldn't verify this bank account. Try linking a different bank account.",
        },
      },
    },
    successPage: {
      depositDelay:
        '*{{paymentType}} deposits may take up to 5 minutes to be reflected in your account.',
      error:
        'Unfortunately, there was an issue processing your deposit. Please try again later or contact us.',
    },
    crypto: {
      title: 'Crypto Deposit',
      description: 'Complete your crypto deposit using the checkout below.',
      verifying: 'Verifying your payment...',
      processing: 'Your deposit is being processed...',
      success: {
        title: 'Deposit Initiated',
        message:
          'Your crypto deposit has been initiated. It may take a few minutes to be confirmed on the blockchain and reflected in your account.',
      },
      error: {
        title: 'Deposit was not successful!',
        message: 'Your wallet was not charged. Please try again.',
      },
      loading: 'Preparing checkout...',
      tryAgain: 'Try again',
      contactSupport: 'Contact Support',
    },
    edgeBoost: {
      title: 'EDGE BOOST',
      description:
        'Link your EDGE Boost account to deposit instantly, with no fees and a balance bonus.',
      highlights: {
        instant: {
          title: 'Instant & Free, 24/7/365',
          description: 'No fees, no minimum balance · nights, weekends, holidays',
        },
        limit: {
          title: 'Up to $1,000,000',
          description: 'Per transaction · $10M daily',
        },
      },
      howItWorks: 'How It Works',
      steps: {
        one: 'Add funds to your <bold>EDGE account</bold> instantly and free.',
        two: 'Link your <bold>Splash email and username</bold> to pair your accounts.',
        three: 'Push funds from EDGE Boost to Splash. <bold>Funds post within 24hrs.</bold>',
      },
      footerNote: "You'll be securely connected to EDGE Boost ({{url}}) to link your account.",
      continue: 'Continue to EDGE Boost',
    },
    cryptoDirect: {
      title: 'CRYPTO DEPOSIT', // <!-- COPY -->
      copy: '$10 minimum. Credited in USD once confirmed on-chain.', // <!-- COPY -->
      catalogEmpty: 'No deposit networks are available right now. Please try again in a moment.', // <!-- COPY -->

      address: {
        label: 'Your {{network}} deposit address', // <!-- COPY -->
        qrAlt: 'QR code for your {{network}} deposit address', // <!-- COPY -->
        copy: 'Copy address', // <!-- COPY -->
        copied: 'Copied', // <!-- COPY -->
        copiedAnnouncement: 'Deposit address copied to clipboard', // <!-- COPY -->
      },
      suspended: {
        title: 'Crypto deposits are paused on your account', // <!-- COPY -->
        message: 'Please <supportLink>contact support</supportLink> for help with your account.', // <!-- COPY -->
      },
      provisioningFailed: {
        title: 'We couldn’t finish setting up crypto deposits', // <!-- COPY -->
        message:
          'We hit a limit while creating your crypto deposit address. Please contact support@splashsports.com and we’ll get it sorted.', // <!-- COPY -->
        supportCta: 'Contact support', // <!-- COPY -->
      },
      error: {
        retry: 'Try again', // <!-- COPY -->
      },
      breadcrumb: {
        // EDGE-32114 removed the "‹ Crypto deposit" breadcrumb entirely. These two contextual back
        // labels are retained and consumed by the gutter BackButtons' aria-labels in EDGE-32128
        // (network/asset list steps).
        backToMethods: 'Back to deposit methods', // <!-- COPY -->
        backToNetworks: 'Back to networks', // <!-- COPY -->
      },
      sheet: {
        title: '{{asset}} on {{network}}', // <!-- COPY -->
        back: 'Back', // <!-- COPY -->
        backToAssets: 'Back to {{network}} assets', // <!-- COPY -->
        close: 'Close', // <!-- COPY -->

        reveal: 'Show my deposit address', // <!-- COPY -->
        warning: 'Send only {{asset}} on {{network}}. Anything else is lost.', // <!-- COPY -->
        minimum: '$10 minimum · non-refundable once sent', // <!-- COPY -->
        expectedTime:
          'Crypto deposits usually arrive within a few minutes. Some networks can take longer depending on on-chain traffic.', // <!-- COPY -->
        error: 'Could not create your address. Try again.', // <!-- COPY -->
        loading: 'Creating your deposit address…', // <!-- COPY -->

        revealed: 'Your {{asset}} deposit address on {{network}} is ready. Copy it below.', // <!-- COPY -->

        cooldown: 'Too many attempts. Try again after {{time}}.', // <!-- COPY -->
        cooldownUnknown: 'Too many attempts. Try again later.', // <!-- COPY -->
        settingUp: 'Setting up your deposit account…', // <!-- COPY -->
        settingUpTimeout: 'Still setting up. Try again in a moment.', // <!-- COPY -->
        networkUnavailable:
          "This network isn't available for your account yet. Pick another network.", // <!-- COPY -->
        networkUnavailableAction: 'Choose another network', // <!-- COPY -->
        // EDGE-32497 (design §2.3): shown for 30 days after a deposit address rotation. Address-free
        // on purpose — more than one prior address may exist and all of them still credit.
        addressChanged:
          'Your {{network}} deposit address was updated on {{date}}. A previous address for {{network}} still works.', // <!-- COPY -->
      },
      arrivals: {
        title: 'Recent transfers', // <!-- COPY -->
        emptyTitle: 'No transfers yet', // <!-- COPY -->
        emptyBody: 'Your crypto deposits will show up here.', // <!-- COPY -->
        underReviewHint:
          'Deposits under review are held while our team confirms them. This can take up to one business day.', // <!-- COPY -->
        status: {
          pending: 'Pending', // <!-- COPY -->
          under_review: 'Under review', // <!-- COPY -->
          completed: 'Completed', // <!-- COPY -->
          returned: 'Returned', // <!-- COPY -->
        },
        completedNotification: {
          title: 'Deposit received', // <!-- COPY -->
          message: '{{amount}} has been added to your balance.', // <!-- COPY -->
        },
      },
    },
    tooltip: {
      disabledPaymentMethod: '{{paymentMethod}} is currently unavailable',
    },
  },
  deleteAeropayAccount: {
    success: {
      message: 'Aeropay account deleted successfully',
    },
    error: {
      message: 'Failed to delete Aeropay account',
    },
  },

  withdrawal: {
    requirements: {
      title: 'Before you withdraw',
      intro: 'Complete both of these before you can withdraw. You can do them in either order.',
      w9: { label: 'Tax form', action: 'Complete tax form' },
      docv: { label: 'Photo ID check', action: 'Verify your ID' },
      status: {
        outstanding: 'Not started',
        pending: 'Checking your ID',
        cleared: 'Done',
      },
      unavailable: {
        title: 'We cannot check this right now',
        copy: 'Please try again in a moment.',
      },
      continue: 'Continue to withdrawal',
    },
    verifyIdentity: {
      title: 'Verify your ID',
      intro:
        'This withdrawal needs a photo ID check. You will be taken to our verification partner to photograph your ID, then brought back here to finish.',
      action: 'Start ID check',
      pendingTitle: 'Checking your ID',
      pendingCopy: 'This takes a few seconds. Keep this page open.',
      retryTitle: 'We did not get a result',
      retryCopy: 'The ID check was not completed. You can try again.',
      retryAction: 'Try again',
      stillCheckingTitle: 'Your ID check is still in progress',
      stillCheckingCopy:
        'If you finished it, it may need a manual review. Check back later, or start again if you did not finish.',
      stillCheckingAction: 'Start again',
      reviewTitle: 'Your ID is being reviewed',
      reviewCopy: 'Someone is taking a look. We will let you know as soon as it is done.',
      refusedTitle: 'We could not verify your ID',
      refusedCopy: 'Contact support and we will help you sort it out.',
      errorTitle: 'We could not start the ID check',
      errorStartFailed: 'Please try again in a moment.',
    },
    taxInformation: {
      stepTitles: {
        step1: 'Please complete W-9 Tax Form',
        step2: 'Taxpayer Identification Form',
        step3: 'W-9 Submitted',
      },
      common: {
        introCopy:
          "To comply with the US federal regulations you're required to fill out a W-9 form when you reach $2,000 or more in winnings. If your net winnings are less than $2,000 at the end of the calendar year no tax documents will be filed by Splash Sports.",
        taxDisclaimer:
          'Splash Sports does not provide tax, legal, or accounting advice. You should consult your own tax, legal, and accounting advisors for specific tax advice.',
        continueWithdrawal: 'Continue withdrawal',
        genericError: 'Something went wrong. Please try again.',
      },
      newUser: {
        completeTaxForm: 'Complete tax form',
        success:
          'Thank you for completing your W-9 form. You may now proceed with your withdrawal request.',
      },
      returningUser: {
        title: 'Please confirm tax information',
        reviewTitle: 'Review your information',
        successTitle: 'Tax information confirmed',
        intro:
          'You already have a W-9 on file from a previous year. Before you can withdraw funds, please confirm that your personal information is still accurate.',
        confirmTaxInformation: 'Confirm tax information',
        makeNameAddressCorrect: 'Make sure your name and address are correct.',
        contactSupportBefore: 'Is something incorrect? Please',
        contactSupportLink: 'contact support',
        contactSupportAfter: 'to get your information updated before proceeding.',
        disclosureCopy:
          'At the end of the calendar year Splash Sports will send a 1099 to your mailing address if your net winnings are $2,000 or more.',
        importantDisclosure: 'Important Disclosure Information',
        confirmCheckbox:
          'By completing this form I confirm that the information listed above is accurate.',
        success:
          'Thank you for confirming your information. You may now proceed with your withdrawal request.',
      },
      form: {
        intro:
          "To comply with the US federal regulations, you're required to fill out a W\u20119 form.",
        confirmAreTitle: 'Confirm that you are:',
        citizenItems: {
          citizen: 'a U.S. citizen (sole or multiple); or',
          resident: 'a permanent resident or green-card holder; or',
          substantialPresence: 'a U.S. resident under the',
          substantialPresenceLink: 'substantial presence test',
        },
        citizenConfirm: 'Yes, I confirm.',
        perjuryTitle: 'Under penalties of perjury, I certify that:',
        perjuryItems: {
          item1: 'The number shown on this form is my correct taxpayer identification number; and',
          item2: 'I am not subject to backup withholding because:',
          item2a: 'I am exempt from backup withholding, or',
          item2b:
            'I have not been notified by the Internal Revenue Service (IRS) that I am subject to backup withholding as a result of a failure to report all interest or dividends, or',
          item2c: 'The IRS has notified me that I am no longer subject to backup withholding;* and',
          item3Before: 'I am a U.S. citizen or other U.S. person, as defined by',
          item3LinkLabel: 'https://www.irs.gov/pub/irs-pdf/iw9.pdf',
          item3After: '; and',
          item4:
            'The FATCA code(s) entered on this form (if any) indicating that I am exempt from FATCA reporting is correct.*',
        },
        perjuryDisclaimer:
          '*#2 and #4 are not applicable for activity on the Splash Sports platform.',
        addressTitle: 'Name and mailing address:',
        mailDisclosure:
          'Following the submission of this tax form, Splash Sports will mail a 1099 tax form to your mailing address.',
        importantDisclosure: 'Important disclosure information',
        signTitle: 'eSign with your SSN or ITIN',
        ssnLabel: 'Enter your SSN or ITIN:',
        confirmSsnLabel: 'Re-enter your SSN or ITIN to confirm:',
        submit: 'Submit my W-9',
        errors: {
          terms: 'You must agree to the terms.',
          ssnFormat: 'Invalid SSN format. Format should be 123-45-6789.',
          ssnMatch: 'SSN fields must match.',
        },
      },
    },
    /** @deprecated - remove when legacy withdrawal form is removed */
    completedLegacy: {
      title: 'Funds withdrawn',
      fundsWithdrawn: 'has been withdrawn from your account',
      newBalance: `Your new balance is {{amount}}`,
      continue: `Continue`,
    },
    completed: {
      title: 'Success!',
      fundsWithdrawn: 'is being transferred to your {{ paymentType }}.',
      newBalance: `Your remaining balance is {{amount}}`,
      continue: `Continue`,
    },
    form: {
      select: {
        title: 'Withdraw Method',
        copy: 'Select your withdrawal method. You can only withdraw funds using methods that you’ve used to deposit funds.',
        labelSaved: 'Saved',
        labelFree: 'Free methods',
        labelPaid: 'Other methods',
      },
      withdraw: {
        title: 'Withdrawal Amount',
        copy: 'Enter your withdrawal amount.',
        labelSaved: 'Saved',
        labelFree: 'Free methods',
        labelPaid: 'Other methods',
      },
      title: 'Withdraw Funds',
      paymentType: {
        label: 'Payment Type',
      },
      amount: {
        label: 'Withdrawal Amount',
        placeholder: '$10.00',
        error: 'Please enter a valid amount',
      },
      card: {
        label: 'Select Card',
      },
      fees: {
        title: 'Withdrawal Fees',
        amount: 'Fee Amount',
      },
      actions: {
        continue: 'Continue',
        withdraw: 'Withdraw {{amount}}',
        tooltip: {
          paymentType: 'Please select a payment type',
          minimum: 'Withdrawal requests must be for at least {{amount}}.',
          insufficientBalance: 'Insufficient balance',
          disabledPaymentType: 'Selected payment type is not supported.',
          emptyCard: 'Card must be selected for withdrawal.',
          emptyCvv: 'CVV must be filled for confirmation.',
        },
        withdrawToCheck: {
          message: 'I would like to withdraw {{amount}} from my balance of {{balance}}',
          mailTitle: 'Withdrawal Request',
          mailMessage: 'Hello, Splash Support - {{message}}',
        },
      },
    },
    summary: {
      withdrawalAmount: 'Withdrawal amount',
      serviceFee: 'Service fee',
      transferAmount: 'Transfer amount',
      remainingBalance: 'Remaining balance',
      remainingBalanceAfterFees: 'Remaining balance after fees',
    },
    fields: {
      amount: {
        label: 'Withdrawal Amount',
        placeholder: '$10.00',
      },
    },
    warnings: {
      cannotWithdrawToCreditCard:
        'For your security, we require a successful debit card deposit before withdrawing to a debit card. Please make a deposit or use one of our other withdrawal methods if you do not have a saved Debit Card.',
      missingKYC: 'We need a little more information from you before you can withdraw.',
    },
    errors: {
      blockedByFeatureFlag: 'Unfortunately, you are not currently allowed to make a withdrawal.',
      blockedByLocation:
        'Unfortunately, you are not currently allowed allowed to make a withdrawal, based on your location.',
      unavailablePaymentType:
        'Unfortunately, withdrawing with {{type}} is not currently available at your location.',
      noAvailableMethods:
        'It looks like there are no available withdrawal methods in your location.',
    },
    descriptions: {
      default: 'Processing fee is {{ fee }}',
      card: 'Withdrawals via debit card will include a {{ fee }} service fee. The service fee will be deducted from the withdrawal amount you enter.',
      paperCheck: 'Withdrawals via check are subject to a {{ fee }} service fee.',
    },
    canada: {
      canadianDollars: 'You will receive Canadian dollars in your account.',
      bankFees: 'Some banks charge processing fees on credit card transactions.',
    },
  },
  errors: {
    insufficientBalance: 'Insufficient balance',
    USER_NOT_AUTHORIZED: {
      title: 'Unauthorized',
      body: 'Please log in.',
    },
    KYC_ALREADY_APPROVED: {
      title: "You've already verified your identity",
      body: 'Go to the contest lobby to check out our contests!',
    },
    KYC_ID_REFERRED: {
      title: 'We need more information',
      body: '',
    },
    KYC_MANUAL_REVIEW: {
      title: 'We need more information to verify your identity',
      body: 'Please contact support if you are having any issues.',
    },
    KYC_REJECTED: {
      title: 'We are unable to verify your identity',
      body: 'Please contact support if you believe there is an issue.',
    },
    ACTIVE_TIMEOUT_EXISTS: {
      title: 'Timeout already applied',
      body: "You've already opted into a timeout.",
    },
    USER_ALREADY_EXISTS: {
      title: 'Duplicate Account',
      body: 'It appears you already have another account. Please contact support for details. ',
    },
    PAYPAL_PAYER_ID: {
      title: 'Invalid request',
      body: "You must first deposit with PayPal before you're able to withdraw to your PayPal account.",
    },
    DUPLICATE_PAYPAL_ACCOUNT: {
      title: 'Invalid PayPal Account',
      body: 'It looks like this PayPal account has already been linked to another Splash User.',
    },
    INSUFFICIENT_BALANCE: {
      title: 'Withdraw-able Balance Exceeded',
      body: "You've attempted to withdraw more than your withdraw-able balance.",
    },
    LIMIT_ALREADY_EXISTS: {
      title: 'Limit already set',
      body: 'You have already this limit set.',
    },
    LIMIT_NOT_FOUND: {
      title: 'Limit not found',
      body: 'The limit does not exist.',
    },
    USER_DEPOSIT_LIMIT_EXCEEDED: {
      title: 'Limit exceeded',
      body: '',
    },
    LOCATION_DEPOSIT_LIMIT_EXCEEDED: {
      title: 'State Deposit Limit',
      body: 'Based on your current location, you are not able to deposit more.',
    },
    MONTHLY_LIMIT_LESS_THAN_WEEKLY: {
      title: 'Invalid Entry',
      body: 'Monthly Limit cannot be less than Weekly limit.',
    },
    WEEKLY_LIMIT_MORE_THAN_MONTHLY: {
      title: 'Invalid Entry',
      body: 'Weekly Limit cannot be greater than Monthly limit.',
    },
    CONTEST_ENTRY_LIMIT: {
      title: 'Limit Reached',
      body: "You've reached your contest entry limit",
    },
    CONTEXT_ENTRY_FEE_LIMIT: {
      title: 'Limit Reached',
      body: "You've reached your contest entry limit",
    },
    USER_NOT_KYC_APPROVED: {
      title: 'Please verify your identity.',
      body: 'Before you are able to deposit or enter a contest, you must finish verifying your identity.',
    },
    INVALID_USER_STATUS: {
      title: 'Please verify your identity.',
      body: 'Before you are able to deposit or enter a contest, you must finish verifying your identity.',
    },
    DEPOSIT_AMOUNT_BELOW_MINIMUM: {
      title: 'Minimum Deposit',
      body: 'Must deposit at least {amount}',
    },
    WITHDRAWAL_AMOUNT_BELOW_MINIMUM: {
      title: 'Minimum Withdrawal',
      body: 'Must withdraw at least {amount}',
    },
    USER_BALANCE_BELOW_MINIMUM_BALANCE: {
      title: 'Minimum Account Balance',
      body: 'Must have an account balance of at least {amount}',
    },
    PAYMENT_METHOD_DISABLED: {
      title: 'Payment Method Disabled',
      body: 'This Payment method is not currently available.',
    },
    USER_NOT_FOUND: {
      title: "We're having an issue",
      body: 'Please refresh and try again.',
    },
    LOCATION_TOKEN_REQUIRED: {
      title: 'Invalid Location',
      body: 'Please make sure you have location services enabled and ad-blockers disabled.',
    },
    LOCATION_TOKEN_INVALID: {
      title: 'Invalid Location',
      body: 'Please contact support.',
    },
    LOCATION_TOKEN_CONFLICT: {
      title: 'Invalid Location',
      body: 'Please contact support.',
    },
    LOCATION_ERROR: {
      title: 'Invalid Location',
      body: 'We are not yet active in your current location.',
    },
    AGE_RESTRICTION: {
      title: 'Below Minimum Age',
      body: 'You do not meet the minimum age requirements for your state.',
    },
    ACTIVE_TIMEOUT: {
      title: 'Timeout on account',
      body: 'There is an active timeout on your account.',
    },
    WHOP_CRYPTO_ACCOUNT_SUSPENDED: {
      title: 'Crypto deposits paused', // <!-- COPY -->
      body: 'Crypto deposits are paused on your account. Please contact support.', // <!-- COPY -->
    },
    WHOP_CRYPTO_PROVISIONING: {
      title: 'Almost ready', // <!-- COPY -->
      body: 'Your deposit address is still being prepared. Please try again in a moment.', // <!-- COPY -->
    },
    WHOP_CRYPTO_UNAVAILABLE: {
      title: 'Crypto deposits unavailable', // <!-- COPY -->
      body: 'Crypto deposits are temporarily unavailable. Please try again later.', // <!-- COPY -->
    },
    WHOP_CRYPTO_ACCOUNT_CONFLICT: {
      title: 'Crypto deposits unavailable', // <!-- COPY -->
      body: 'Crypto deposits are temporarily unavailable. Please try again later.', // <!-- COPY -->
    },
    WHOP_CRYPTO_PROVIDER_REJECTED: {
      title: 'Crypto deposits unavailable', // <!-- COPY -->
      body: 'Crypto deposits are temporarily unavailable. Please try again later.', // <!-- COPY -->
    },
    WHOP_CRYPTO_INVALID_SINCE: {
      title: 'Crypto deposits unavailable', // <!-- COPY -->
      body: 'Crypto deposits are temporarily unavailable. Please try again later.', // <!-- COPY -->
    },
    WHOP_CRYPTO_ACCOUNT_MISSING: {
      title: 'Crypto deposits unavailable', // <!-- COPY -->
      body: 'Crypto deposits are temporarily unavailable. Please try again later.', // <!-- COPY -->
    },
    WHOP_CRYPTO_METHOD_UNAVAILABLE: {
      title: 'Not available in your location', // <!-- COPY -->
      body: "Crypto deposits aren't available in your location yet.", // <!-- COPY -->
    },
    UNKNOWN: {
      title: "We're having an issue",
      body: 'Please contact support.',
    },
    generic: {
      transactionHistory: {
        title: 'Error',
        message: 'There was an error retrieving your transaction history',
      },
      withdrawal: {
        title: 'Withdrawal Error',
        verificationRequired:
          'We need to verify your identity before this withdrawal can continue.',
        taxAndVerificationRequired:
          'We need your tax information and to verify your identity before this withdrawal can continue.',
        eligibilityUnavailable:
          "We couldn't check this withdrawal right now. Please try again in a moment.",
      },
    },
  },
};
